/**
 * The property catalog for the accounts in scope: registry entries (including
 * the solution narrative and operations aliases) plus recorded on-site visits.
 *
 * Scoping: both reads go through customer views whose RLS already limits a
 * customer to properties their own locations link to, and the query additionally
 * filters to the codes those locations carry. Staff can read every row, so the
 * explicit filter is what keeps a staff session in view-as mode honest.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';
import {
  PROPERTY_DIRECTORY_COLUMNS,
  toDirectoryEntry,
  type PropertyDirectoryEntry,
  type PropertyDirectoryRow,
} from '../lib/propertyDirectory';
import {
  buildPropertyCatalog,
  EMPTY_CATALOG,
  PROPERTY_VISIT_COLUMNS,
  toVisit,
  type PropertyCatalog,
  type PropertyVisit,
  type PropertyVisitRow,
} from '../lib/propertyCatalog';
import { useLocations } from './useLocations';

/**
 * Property codes carried by the in-scope locations. The catalog is keyed on
 * these, so an account only ever pulls the properties it actually operates.
 */
export function usePropertyCodesInScope(): { codes: string[]; ready: boolean } {
  const locationsQuery = useLocations();
  const codes = useMemo(() => {
    const locations = locationsQuery.data?.locations ?? [];
    return [
      ...new Set(
        locations
          .map((location) => location.hierarchy?.property_code?.trim())
          .filter((code): code is string => !!code),
      ),
    ].sort();
  }, [locationsQuery.data]);

  return { codes, ready: locationsQuery.isSuccess };
}

export interface PropertyCatalogResult {
  catalog: PropertyCatalog;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export function usePropertyCatalog(): PropertyCatalogResult {
  const scope = useCustomerScope();
  const { codes, ready } = usePropertyCodesInScope();

  const query = useQuery({
    queryKey: ['property-catalog', scope.key, codes.join(',')],
    enabled: scope.ready && ready && codes.length > 0,
    queryFn: async (): Promise<PropertyCatalog> => {
      const [directory, visits] = await Promise.all([
        supabase
          .from('property_directory_customer_view')
          .select(PROPERTY_DIRECTORY_COLUMNS)
          .in('code', codes),
        supabase
          .from('property_visit_history_customer_view')
          .select(PROPERTY_VISIT_COLUMNS)
          .in('property_code', codes)
          .order('occurred_on', { ascending: false }),
      ]);

      if (directory.error) throw directory.error;
      // Visit history is additive: a property with no recorded visits, or a
      // database that has not run the catalog migration yet, still renders.
      const visitRows = visits.error ? [] : ((visits.data ?? []) as unknown as PropertyVisitRow[]);

      const entries = ((directory.data ?? []) as unknown as PropertyDirectoryRow[])
        .map(toDirectoryEntry)
        .filter((entry): entry is PropertyDirectoryEntry => !!entry);

      const parsedVisits = visitRows
        .map(toVisit)
        .filter((visit): visit is PropertyVisit => !!visit);

      return buildPropertyCatalog(entries, parsedVisits);
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    catalog: query.data ?? EMPTY_CATALOG,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
